import { useState, useEffect, useCallback } from "react";
import api from "../../../utils/api/axiosInstance";
import { fetchCategoryDetails } from "../../../hooks/useCategoryDetails";

const isValidProductPayload = (data) =>
  Boolean(data && data._id && (data.name || data.model || data.brand));

export default function useProductDetails(id) {
  const [product, setProduct] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryFields, setCategoryFields] = useState([]);

  const fetchProduct = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    setProduct(null);
    setSimilar([]);
    setCategoryFields([]);

    try {
      const productRes = await api.get(`/products/${id}`);
      const productData = productRes.data;

      if (!isValidProductPayload(productData)) {
        setError("Product not found.");
        return;
      }

      setProduct(productData);

      const categoryId =
        productData.category?._id || productData.category || null;

      if (categoryId) {
        try {
          const categoryDetails = await fetchCategoryDetails(categoryId);
          setCategoryFields(
            categoryDetails.allFields || categoryDetails.fields || [],
          );
        } catch (err) {
          console.error("Failed to fetch category fields:", err);
          setCategoryFields([]);
        }

        try {
          const similarRes = await api.get(`/products/category/${categoryId}`, {
            params: { limit: 12 },
          });
          const similarProducts = Array.isArray(similarRes.data)
            ? similarRes.data
            : similarRes.data?.products || [];
          setSimilar(
            similarProducts.filter((p) => p._id !== productData._id),
          );
        } catch (err) {
          console.error("Failed to fetch similar products:", err);
          setSimilar([]);
        }
      }
    } catch (err) {
      console.error("Error fetching product:", err);
      if (err.response?.status === 403) {
        setError("This product is no longer available.");
      } else if (err.response?.status === 404) {
        setError("Product not found.");
      } else {
        setError("Failed to load product.");
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  return {
    product,
    similar,
    loading,
    error,
    categoryFields,
    refetch: fetchProduct,
  };
}
